// Chemin: C:\gestion-copro\src\components\layout\VigieLayout.tsx
// Layout pour l'espace VIGIE — Copropriétaire autonome
// Session 100 : Chantier ESCALATOR — Création
// Session 118 : SENTINELLE = page d'accueil. /vigie redirige vers /vigie/sentinelle.
// Session 121 : Sidebar enrichie — ajout "Mes contacts"
// Session 256 : Suppression "Mes correspondances" — GED unifiée (tout dans "Mes documents")
// Session 123 : ajout "Mes signalements" dans la sidebar
// Session 125 : ajout "Mes dossiers" dans la sidebar (module affaires VIGIE)
// Session 130 : ajout "Mon calendrier" dans la sidebar (module échéances VIGIE)
// Session 149 : ajout "Aide / FAQ" dans la sidebar + BoutonAideFlottant
// Session 151 : suppression BoutonAideFlottant (gêne dictée iPad) + ajout "Nous contacter" dans sidebar
// Session 180 : "Mon calendrier" → "Mes échéances" (label plus parlant pour l'utilisateur)
// Session 185 : ajout "Exporter mes données" dans la sidebar (RGPD portabilité)
// Session 186 : Refonte sidebar — icônes Lucide, groupes visuels, design moderne
// Session 188 : ajout lien "Espace Partenaire" si apporteurInfo présent (double rôle vigie+apporteur)
// Session 217 : ajout "Ma comptabilité" dans le groupe principal
// Session 217B : ajout "Suivi bailleur" conditionnel (vigieInfo.estBailleur === true)
// Session 235 : suppression "Mes signalements" de la sidebar — tout passe par "Mes dossiers"
// Session 245 : ajout "Ma copropriété" (profil + tantièmes) dans le groupe principal
// Session 265 : suppression "Suivi bailleur" de la sidebar — absorbé dans "Ma comptabilité" (onglet Ma location)
//               ajout "Mon profil" dans le menu avatar (dropdown)

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { 
  Shield, 
  ChevronDown, 
  LogOut, 
  ArrowUpCircle,
  User,
  LayoutDashboard,
  Users,
  FolderOpen,
  CalendarClock,
  FileText,
  HelpCircle,
  BookOpen,
  MessageSquare,
  Download,
  Handshake,
  Calculator,
  Building2,
  Settings
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { USER_ROLE_LABELS, UserRole } from '../../types/user.types';

// ============================================
// TYPES
// ============================================

interface VigieLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

// ============================================
// NAVIGATION VIGIE — Groupes avec icônes Lucide
// Session 186 : Refonte complète — 3 groupes, icônes Lucide, hiérarchie visuelle
// ============================================

const iconClass = "w-[18px] h-[18px]";

// Session 235 : suppression "Mes signalements" — tout passe par "Mes dossiers"
// Session 245 : ajout "Ma copropriété" dans le groupe principal
// Session 265 : suppression "Suivi bailleur" — absorbé dans "Ma comptabilité"
function buildNavGroups(): NavGroup[] {
  const principalItems: NavItem[] = [
    { path: '/vigie/sentinelle', label: 'SENTINELLE', icon: <Shield className={iconClass} /> },
    { path: '/vigie/dashboard', label: 'Tableau de bord', icon: <LayoutDashboard className={iconClass} /> },
    { path: '/vigie/contacts', label: 'Mes contacts', icon: <Users className={iconClass} /> },
    { path: '/vigie/dossiers', label: 'Mes dossiers', icon: <FolderOpen className={iconClass} /> },
    { path: '/vigie/calendrier', label: 'Mes échéances', icon: <CalendarClock className={iconClass} /> },
    { path: '/vigie/documents', label: 'Mes documents', icon: <FileText className={iconClass} /> },
    { path: '/vigie/comptabilite', label: 'Ma comptabilité', icon: <Calculator className={iconClass} /> },
    // Session 245 : Ma copropriété — profil complet (syndic, promoteur, lots, tantièmes)
    { path: '/vigie/profil', label: 'Ma copropriété', icon: <Building2 className={iconClass} /> },
  ];

  return [
    {
      // Groupe principal — Outils de travail
      items: principalItems,
    },
    {
      // Groupe support
      label: 'Support',
      items: [
        { path: '/vigie/faq', label: 'Aide / FAQ', icon: <HelpCircle className={iconClass} /> },
        { path: '/vigie/kit-demarrage', label: 'Kit de démarrage', icon: <BookOpen className={iconClass} /> },
        { path: '/vigie/nous-contacter', label: 'Avis & idées', icon: <MessageSquare className={iconClass} /> },
      ],
    },
    {
      // Groupe données personnelles
      label: 'Mes données',
      items: [
        { path: '/vigie/export-donnees', label: 'Exporter mes données', icon: <Download className={iconClass} /> },
      ],
    },
  ];
}

// ============================================
// URL LOGO COPRO PROTECT
// ============================================

const LOGO_COPRO_PROTECT_URL = '/logo-copro-protect.png';

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const VigieLayout: React.FC<VigieLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { userProfile, signOut } = useAuth();
  const { copropriete, loading: coproprieteLoading, setCoproprieteId, coproprieteId } = useCopropriete();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Session 104 : Si ?copro= dans l'URL (Admin SaaS accède à une copro VIGIE), changer la copro active
  useEffect(() => {
    const coproFromUrl = searchParams.get('copro');
    if (coproFromUrl && coproFromUrl !== coproprieteId) {
      setCoproprieteId(coproFromUrl);
    }
  }, [searchParams, coproprieteId, setCoproprieteId]);

  // Vérifier si l'utilisateur est Admin SaaS (Dominique qui teste le parcours VIGIE)
  const isAdminSaas = userProfile?.role === 'admin_saas' || userProfile?.isGestionnaireSaas === true;

  // Session 188 : Vérifier si l'utilisateur est aussi apporteur (double casquette)
  const hasApporteurRole = !!userProfile?.apporteurInfo?.identifiant;

  const navGroups = buildNavGroups();
  const allNavItems = navGroups.flatMap(g => g.items);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Initiale utilisateur pour l'avatar
  const userInitial = userProfile?.prenom?.charAt(0)?.toUpperCase() || 
                      userProfile?.email?.charAt(0)?.toUpperCase() || 
                      'U';

  // Nom affiché
  const displayName = userProfile?.prenom || 
                      userProfile?.email?.split('@')[0] || 
                      'Utilisateur';

  // Nom de la copropriété (depuis vigieInfo ou copropriete Firestore)
  const rawNom = userProfile?.vigieInfo?.nomCopropriete || copropriete?.nom || 'Ma copropriete';
  const nomCopropriete = typeof rawNom === 'string' ? rawNom : (rawNom as any)?.nom || 'Ma copropriete';

  // Détection item actif
  const isItemActive = (path: string) => {
    if (path === '/vigie/sentinelle') return location.pathname === '/vigie/sentinelle';
    if (path === '/vigie/dashboard') return location.pathname === '/vigie/dashboard';
    return location.pathname.startsWith(path);
  };

  // ============================================
  // Session 118 : Redirection /vigie → /vigie/sentinelle
  // ============================================
  if (location.pathname === '/vigie' || location.pathname === '/vigie/') {
    return <Navigate to="/vigie/sentinelle" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header — bg-blue-900 conforme à la charte graphique (#1E40AF = primary-600) */}
      <header className="fixed top-0 left-0 right-0 h-[60px] bg-blue-900 text-white z-50 flex items-center px-4 shadow-md">
        <div className="flex items-center justify-between w-full">
          {/* Gauche : Logo + nom copropriété */}
          <div className="flex items-center gap-3">
            {/* Bouton hamburger mobile */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden flex flex-col justify-center gap-1.5 w-8 h-8 focus:outline-none"
            >
              <span className={`block h-0.5 w-5 bg-white rounded transition-transform ${sidebarOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block h-0.5 w-5 bg-white rounded transition-opacity ${sidebarOpen ? 'opacity-0' : ''}`} />
              <span className={`block h-0.5 w-5 bg-white rounded transition-transform ${sidebarOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>

            {/* Logo */}
            <Link to="/vigie/sentinelle" className="flex items-center gap-2">
              <img src={LOGO_COPRO_PROTECT_URL} alt="Copro Protect" className="h-8 w-auto" />
            </Link>

            {/* Nom copropriété (desktop) */}
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-white/20">
              <span className="text-xs text-white/60 uppercase tracking-widest font-semibold">VIGIE</span>
              <span className="text-sm text-white/80 font-medium truncate max-w-[200px]">{nomCopropriete}</span>
            </div>
          </div>

          {/* Droite : Avatar + dropdown */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
            >
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                {userInitial}
              </div>
              <span className="hidden sm:block text-sm font-medium text-white/90">{displayName}</span>
              <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {dropdownOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                  {/* Infos user */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800">{displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{userProfile?.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                      {USER_ROLE_LABELS[userProfile?.role as UserRole] || 'VIGIE'}
                    </span>
                  </div>
                  
                  <div className="p-2">
                    {/* Lien Admin SaaS (mobile) */}
                    {isAdminSaas && (
                      <Link
                        to="/admin-saas"
                        onClick={() => setDropdownOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors text-sm sm:hidden"
                      >
                        Admin SaaS
                      </Link>
                    )}

                    {/* Session 188 : Lien Espace Partenaire (visible si apporteur) */}
                    {hasApporteurRole && (
                      <Link
                        to="/apporteur"
                        onClick={() => setDropdownOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm font-medium"
                      >
                        <Handshake className="w-4 h-4" />
                        Espace Partenaire
                      </Link>
                    )}

                    <Link
                      to="/vigie/profil"
                      onClick={() => setDropdownOpen(false)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-sm font-medium"
                    >
                      <User className="w-4 h-4" />
                      Mon profil
                    </Link>

                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                    >
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-[60px] left-0 bottom-0 w-64 bg-white border-r border-gray-200 z-40 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <nav className="flex flex-col h-full overflow-y-auto">
          {/* Groupes de navigation */}
          <div className="flex-1 py-3">
            {navGroups.map((group, groupIndex) => (
              <div key={groupIndex}>
                {/* Séparateur + Label de groupe */}
                {group.label && (
                  <div className="px-4 pt-5 pb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                        {group.label}
                      </span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                  </div>
                )}

                {/* Items du groupe */}
                <div className="px-3 space-y-0.5">
                  {group.items.map((item) => {
                    const isActive = isItemActive(item.path);
                    const isSentinelle = item.path === '/vigie/sentinelle';
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`
                          group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150
                          ${isSentinelle && isActive
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md shadow-amber-200/50'
                            : isSentinelle && !isActive
                              ? 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-800 hover:from-amber-100 hover:to-orange-100 border border-amber-200/60'
                              : isActive
                                ? 'bg-blue-900 text-white shadow-sm'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        {/* Icône */}
                        <span className={`
                          flex items-center justify-center w-8 h-8 rounded-lg transition-colors flex-shrink-0
                          ${isSentinelle && isActive
                            ? 'bg-white/20'
                            : isSentinelle && !isActive
                              ? 'bg-amber-200/50 text-amber-700'
                              : isActive
                                ? 'bg-white/10'
                                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200 group-hover:text-gray-700'
                          }
                        `}>
                          {item.icon}
                        </span>

                        {/* Label */}
                        <span className={`
                          text-sm flex-1
                          ${isSentinelle
                            ? 'font-bold tracking-wide'
                            : isActive 
                              ? 'font-semibold' 
                              : 'font-medium'
                          }
                        `}>
                          {item.label}
                        </span>

                        {/* Indicateur actif (point) */}
                        {isActive && !isSentinelle && (
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Session 188 : Lien Espace Partenaire (visible uniquement si apporteur) */}
          {hasApporteurRole && (
            <div className="px-3 pb-2">
              <Link
                to="/apporteur"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl text-blue-800 hover:from-blue-100 hover:to-blue-200 transition-all duration-200 group border border-blue-200/60"
              >
                <span className="flex items-center justify-center w-9 h-9 bg-blue-600 rounded-lg shadow-sm group-hover:scale-105 transition-transform">
                  <Handshake className="w-5 h-5 text-white" />
                </span>
                <div>
                  <p className="text-sm font-bold">Espace Partenaire</p>
                  <p className="text-[11px] text-blue-500 group-hover:text-blue-600 transition-colors">
                    Pipeline & commissions
                  </p>
                </div>
              </Link>
            </div>
          )}

          {/* CTA Escalator — Passer au CS */}
          <div className="p-3 border-t border-gray-100">
            <a
              href="/#formules"
              className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-800 to-blue-900 rounded-xl text-white hover:from-blue-700 hover:to-blue-800 transition-all duration-200 group shadow-sm"
            >
              <span className="flex items-center justify-center w-9 h-9 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg shadow-sm group-hover:scale-105 transition-transform">
                <ArrowUpCircle className="w-5 h-5 text-white" />
              </span>
              <div>
                <p className="text-sm font-bold">Passer au CS</p>
                <p className="text-[11px] text-blue-300 group-hover:text-blue-200 transition-colors">
                  Formules Conseil Syndical
                </p>
              </div>
            </a>
          </div>
        </nav>
      </aside>

      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Contenu principal */}
      <main className="lg:ml-64 pt-[60px] min-h-screen">
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default VigieLayout;
